/**
 * Promotions grid data layer — reads the SQL-backed grid from /api/promo-grid
 * and reshapes it into the structures the Promotions page renders, plus the
 * optimistic write mutations (cells, assortments).
 *
 * Replaces the static WW_FY26_PROMOS / COLES_FY26_PROMOS / *_KEY_DATES imports
 * from data/promo-calendar.js. The reshaped output is intentionally a drop-in
 * for those shapes so the page render logic is unchanged.
 */
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './useApi';

export const FY = 26;
const QK = ['promo-grid', FY];

// Sales lanes the page renders with bespoke rows; every other scope falls back
// to a generic lane so no authored event ever silently vanishes from the grid.
const KNOWN_SALES_SCOPES = new Set(['majans', 'ww', 'coles', 'ww-campaign', 'ww-seasonal', 'coles-campaign']);
const isKnownSalesScope = (s) =>
  KNOWN_SALES_SCOPES.has(s) || (typeof s === 'string' && s.startsWith('school:'));

function humanizeScope(scope) {
  const s = String(scope || '').replace(/^school:/, '').replace(/[-_:]+/g, ' ').trim();
  if (!s) return 'Other';
  // Keep short channel acronyms upper-case (atl, tv), title-case the rest.
  return s.replace(/\b\w+/g, (w) => (w.length <= 3 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1)));
}

function reshape(raw) {
  if (!raw) return null;
  const { accounts = [], assortments = [], cells = [], events = [], marketing = [] } = raw;

  // Index cells by assortment → { measure: { week: value } }.
  const byAsst = new Map();
  for (const c of cells) {
    let d = byAsst.get(c.assortmentId);
    if (!d) { d = {}; byAsst.set(c.assortmentId, d); }
    (d[c.measure] ||= {})[String(c.week)] = c.value;
  }

  const promosFor = (retailer) =>
    assortments
      .filter((a) => a.retailer === retailer)
      .map((a) => ({
        assortmentId: a.assortmentId,
        accountId: a.accountId,
        assortment: a.assortment,
        brand: a.brand,
        ref: a.ref,
        data: byAsst.get(a.assortmentId) || {},
      }));

  const pointMap = (scope) =>
    Object.fromEntries(
      events.filter((e) => e.scope === scope).map((e) => [String(e.startWeek), e.label]),
    );
  const spanList = (scope) =>
    events
      .filter((e) => e.scope === scope)
      .map((e) => ({
        eventId: e.eventId,
        startWeek: e.startWeek,
        endWeek: e.endWeek ?? e.startWeek,
        label: e.label,
        dates: e.dates || undefined,
      }));

  const schoolTermsSpans = {};
  for (const e of events.filter((e) => typeof e.scope === 'string' && e.scope.startsWith('school:'))) {
    const state = e.scope.slice('school:'.length);
    (schoolTermsSpans[state] ||= []).push({
      eventId: e.eventId,
      startWeek: e.startWeek,
      endWeek: e.endWeek ?? e.startWeek,
      label: e.label,
      dates: e.dates || undefined,
    });
  }

  const accountId = (retailer) => accounts.find((a) => a.retailer === retailer)?.accountId ?? null;

  // Generic lane grouping: any event → a span (point events become a 1-week
  // band), grouped by scope. Used for marketing lanes and the unknown-scope
  // fallback so the calendar renders every event regardless of taxonomy.
  const lanesByScope = (filterFn) => {
    const m = new Map();
    for (const e of events.filter(filterFn)) {
      if (!m.has(e.scope)) m.set(e.scope, []);
      m.get(e.scope).push({
        eventId: e.eventId,
        startWeek: e.startWeek,
        endWeek: e.endWeek ?? e.startWeek,
        label: e.label,
        dates: e.dates || undefined,
      });
    }
    return [...m.entries()].map(([scope, spans]) => ({ scope, label: humanizeScope(scope), spans }));
  };

  const majansKeyDates = pointMap('majans');
  const wwKeyDates = pointMap('ww');
  const colesKeyDates = pointMap('coles');

  return {
    accounts,
    wwAccountId: accountId('Woolworths'),
    colesAccountId: accountId('Coles'),
    wwPromos: promosFor('Woolworths'),
    colesPromos: promosFor('Coles'),
    majansKeyDates,
    wwKeyDates,
    colesKeyDates,
    // Pre-merged map for week-header highlighting (Majans wins on collision),
    // so the page doesn't allocate a new object literal every render.
    keyDates: { ...wwKeyDates, ...majansKeyDates },
    wwCampaigns: spanList('ww-campaign'),
    wwSeasonalSpans: spanList('ww-seasonal'),
    colesCampaigns: spanList('coles-campaign'),
    schoolTermsSpans,
    // Marketing events (category='marketing') and any unrecognised sales scope
    // render as generic lanes so the calendar covers every authored event.
    marketingLanes: lanesByScope((e) => e.category === 'marketing'),
    extraSalesLanes: lanesByScope((e) => e.category !== 'marketing' && !isKnownSalesScope(e.scope)),
    marketing,
    eventList: events,
    raw,
  };
}

export function usePromoGrid() {
  const apiFetch = useApi();
  const query = useQuery({
    queryKey: QK,
    queryFn: async () => {
      const res = await apiFetch(`/promo-grid?fy=${FY}`);
      if (!res.ok) throw new Error(`Failed to load promo grid: ${res.status}`);
      return res.json();
    },
    retry: false,
  });
  // Memoize the reshape so rapid cell edits (which mutate the cache via
  // optimistic updates) don't re-bucket the whole grid on every render.
  const grid = useMemo(() => reshape(query.data), [query.data]);
  return {
    grid,
    isLoading: query.isLoading,
    error: query.error,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}

export function usePromoMutations() {
  const apiFetch = useApi();
  const qc = useQueryClient();

  // Cell upsert/clear — optimistic, no refetch (value stored verbatim, so the
  // optimistic patch equals the server result). Keeps rapid editing snappy.
  const saveCell = useMutation({
    mutationFn: async ({ assortmentId, measure, week, value }) => {
      const res = await apiFetch('/promo-cell', {
        method: 'PUT',
        body: JSON.stringify({ assortmentId, measure, fy: FY, week, value }),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`Save failed: HTTP ${res.status} ${t.slice(0, 160)}`);
      }
      return res.json();
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: QK });
      const prev = qc.getQueryData(QK);
      qc.setQueryData(QK, (old) => {
        if (!old) return old;
        const value = vars.value == null ? '' : String(vars.value).trim();
        const cells = (old.cells || []).filter(
          (c) => !(c.assortmentId === vars.assortmentId && c.measure === vars.measure && c.week === vars.week),
        );
        if (value !== '') cells.push({ assortmentId: vars.assortmentId, measure: vars.measure, week: vars.week, value });
        return { ...old, cells };
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(QK, ctx.prev); },
  });

  // Structural changes assign server IDs → invalidate to pull canonical state.
  const addAssortment = useMutation({
    mutationFn: async ({ accountId, assortment, brand, ref }) => {
      const res = await apiFetch('/promo-assortment', {
        method: 'POST',
        body: JSON.stringify({ accountId, assortment, brand, ref }),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`Add failed: HTTP ${res.status} ${t.slice(0, 160)}`);
      }
      return res.json();
    },
    onSettled: () => qc.invalidateQueries({ queryKey: QK }),
  });

  const removeAssortment = useMutation({
    mutationFn: async (assortmentId) => {
      const res = await apiFetch(`/promo-assortment/${assortmentId}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) throw new Error(`Remove failed: HTTP ${res.status}`);
      return true;
    },
    onMutate: async (assortmentId) => {
      await qc.cancelQueries({ queryKey: QK });
      const prev = qc.getQueryData(QK);
      qc.setQueryData(QK, (old) =>
        old ? { ...old, assortments: (old.assortments || []).filter((a) => a.assortmentId !== assortmentId) } : old,
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(QK, ctx.prev); },
    onSettled: () => qc.invalidateQueries({ queryKey: QK }),
  });

  // Events (sales + marketing): upsert + delete. Server assigns the id /
  // natural-key MERGE, so invalidate to pull canonical state.
  const upsertEvent = useMutation({
    mutationFn: async (ev) => {
      const res = await apiFetch('/promo-event', {
        method: 'PUT',
        body: JSON.stringify({ fy: FY, ...ev }),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`Event save failed: HTTP ${res.status} ${t.slice(0, 200)}`);
      }
      return res.json();
    },
    onSettled: () => qc.invalidateQueries({ queryKey: QK }),
  });

  const deleteEvent = useMutation({
    mutationFn: async (eventId) => {
      const res = await apiFetch(`/promo-event/${eventId}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) throw new Error(`Event delete failed: HTTP ${res.status}`);
      return true;
    },
    onMutate: async (eventId) => {
      await qc.cancelQueries({ queryKey: QK });
      const prev = qc.getQueryData(QK);
      qc.setQueryData(QK, (old) =>
        old ? { ...old, events: (old.events || []).filter((e) => e.eventId !== eventId) } : old,
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(QK, ctx.prev); },
    onSettled: () => qc.invalidateQueries({ queryKey: QK }),
  });

  return { saveCell, addAssortment, removeAssortment, upsertEvent, deleteEvent };
}
